// CalendarModal.js
import React, { useState, useRef, useEffect } from "react";

const getCalendarMonth = (date) => {
  if (!date) return new Date();
  if (typeof date === "string") {
    const dateParts = date.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (dateParts) {
      return new Date(Number(dateParts[1]), Number(dateParts[2]) - 1, 1);
    }
  }
  const parsedDate = new Date(date);
  return Number.isNaN(parsedDate.getTime())
    ? new Date()
    : new Date(parsedDate.getFullYear(), parsedDate.getMonth(), 1);
};

const CalendarModal = ({
  startDate,
  endDate,
  onDateChange,
  isVisible,
  onClose,
}) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [tempStartDate, setTempStartDate] = useState(startDate);
  const [tempEndDate, setTempEndDate] = useState(endDate);
  const modalRef = useRef(null);

  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const daysOfWeek = ["S", "M", "T", "W", "T", "F", "S"];

  // Update temp dates when props change
  useEffect(() => {
    setTempStartDate(startDate);
    setTempEndDate(endDate);
  }, [startDate, endDate]);

  useEffect(() => {
    if (isVisible) {
      setCurrentMonth(getCalendarMonth(endDate));
    }
  }, [endDate, isVisible]);

  // Close modal when clicking outside
  useEffect(() => {
    if (!isVisible) return;
    
    const handleClickOutside = (e) => {
      if (modalRef.current && !modalRef.current.contains(e.target)) {
        handleCancel();
      }
    };
    
    // Add a small delay to prevent immediate closing
    const timer = setTimeout(() => {
      document.addEventListener("mousedown", handleClickOutside);
    }, 100);
    
    return () => {
      clearTimeout(timer);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isVisible]);

  // Handle escape key
  useEffect(() => {
    if (!isVisible) return;
    const handleEscape = (e) => {
      if (e.key === "Escape") {
        handleCancel();
      }
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isVisible]);

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const firstDayOfMonth = new Date(year, month, 1).getDay();

    const days = [];

    // Add empty cells for days before the first day of the month
    for (let i = 0; i < firstDayOfMonth; i++) {
      days.push(null);
    }

    // Add all days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }

    return days;
  };

  const isSameDay = (date1, date2) => {
    if (!date1 || !date2) return false;
    const d1 = new Date(date1);
    const d2 = new Date(date2);
    return d1.toDateString() === d2.toDateString();
  };

  const isInRange = (date) => {
    if (!tempStartDate || !tempEndDate || !date) return false;
    const d = new Date(date);
    const start = new Date(tempStartDate);
    const end = new Date(tempEndDate);
    return d >= start && d <= end;
  };

  const handleDateClick = (date) => {
    if (!tempStartDate || (tempStartDate && tempEndDate)) {
      // Starting new selection
      setTempStartDate(date);
      setTempEndDate(null);
    } else if (tempStartDate && !tempEndDate) {
      // Completing the range
      if (date >= tempStartDate) {
        setTempEndDate(date);
      } else {
        setTempStartDate(date);
        setTempEndDate(tempStartDate);
      }
    }
  };

  const navigateMonth = (direction) => {
    setCurrentMonth((prev) => {
      const newMonth = new Date(prev);
      newMonth.setMonth(prev.getMonth() + direction);
      return newMonth;
    });
  };

  const handleOK = () => {
    const formatDate = (date) => {
      if (!date) return null;
      const d = new Date(date);
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
        2,
        "0"
      )}-${String(d.getDate()).padStart(2, "0")}`;
    };

    onDateChange(formatDate(tempStartDate), formatDate(tempEndDate));
    onClose(); // Call onClose after onDateChange
  };

  const handleCancel = () => {
    // Reset temp dates first
    setTempStartDate(startDate);
    setTempEndDate(endDate);
    // Then close the modal
    onClose();
  };

  if (!isVisible) return null;

  const days = getDaysInMonth(currentMonth);

  return (
    <div
      ref={modalRef}
      className="absolute bg-white border border-gray-200 rounded-lg shadow-lg z-50 w-80 top-full left-1/2 transform -translate-x-1/2 mt-1"
      onClick={(e) => e.stopPropagation()} // Prevent event bubbling
    >
      {/* Header with month navigation */}
      <div className="flex items-center justify-between p-3 border-b">
        <button
          onClick={() => navigateMonth(-1)}
          className="p-1 hover:bg-gray-100 rounded"
        >
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
        </button>

        <h3 className="font-medium">
          {months[currentMonth.getMonth()]} {currentMonth.getFullYear()}
        </h3>

        <button
          onClick={() => navigateMonth(1)}
          className="p-1 hover:bg-gray-100 rounded"
        >
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 5l7 7-7 7"
            />
          </svg>
        </button>
      </div>

      {/* Days of week header */}
      <div className="grid grid-cols-7 gap-1 p-3 pb-2">
        {daysOfWeek.map((day) => (
          <div
            key={day}
            className="text-center text-xs font-medium text-gray-500 py-1"
          >
            {day}
          </div>
        ))}
      </div>

      {/* Calendar grid */}
      <div className="grid grid-cols-7 gap-1 px-3 pb-3">
        {days.map((date, index) => {
          if (!date) {
            return <div key={index} className="h-8"></div>;
          }

          const isStart = isSameDay(date, tempStartDate);
          const isEnd = isSameDay(date, tempEndDate);
          const inRange = isInRange(date);
          const isToday = isSameDay(date, new Date());

          let cellClass =
            "h-8 flex items-center justify-center text-sm cursor-pointer ";

          if (isStart || isEnd) {
            cellClass += "bg-[#65558F] z-50 text-white rounded-full ";
          } else if (inRange) {
            cellClass += "bg-[#E8DEF8] text-[#3B0069] rounded -mx-4 ";
          } else if (isToday) {
            cellClass += "bg-gray-100 rounded font-medium ";
          } else {
            cellClass += "hover:bg-gray-100 rounded";
          }

          return (
            <div
              key={index}
              className={cellClass}
              onClick={() => handleDateClick(date)}
            >
              {date.getDate()}
            </div>
          );
        })}
      </div>

      {/* Footer buttons */}
      <div className="flex justify-end space-x-2 p-3 border-t">
        <button
          onClick={handleCancel}
          className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800"
        >
          Cancel
        </button>
        <button
          onClick={handleOK}
          className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
        >
          OK
        </button>
      </div>
    </div>
  );
};

export default CalendarModal;
